<template>
  <v-container fluid class="main">
    <v-row justify="center">
      <v-col lg="6" md="8" sm="12" cols="12">
        <v-card class="network-form-card pa-6" :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">

          <!-- Header -->
          <div class="mb-6">
            <h2 class="network-title">Choose Cryptocurrency</h2>
            <p class="network-subtitle">Select the currency to deposit</p>
          </div>

          <!-- Loading state -->
          <div v-if="loadingNetworks" class="text-center py-8">
            <v-progress-circular indeterminate color="#039DE9" size="40"></v-progress-circular>
            <p class="mt-3 text--secondary">Loading networks...</p>
          </div>

          <!-- Network Grid — columns adjust based on item count -->
          <div
            v-else
            class="network-grid"
            :class="gridClass"
          >
            <div
              v-for="network in ovypaycrypto"
              :key="network.slug"
              class="network-card"
              :class="{
                'network-card--selected': selectedNetwork === network.slug,
                'network-card--dark': $vuetify.theme.dark
              }"
              @click="selectNetwork(network)"
            >
              <!-- Badge -->
              <span
                v-if="getNetworkMeta(network.slug).badge"
                class="network-badge"
                :class="getNetworkMeta(network.slug).badge === 'Fast' ? 'badge--fast' : 'badge--top'"
              >
                {{ getNetworkMeta(network.slug).badge }}
              </span>

              <!-- Icon: use file_url from API, fallback to local -->
              <div class="network-icon-wrap">
                <img
                  :src="network.file_url || getNetworkMeta(network.slug).icon"
                  width="42"
                  height="42"
                  class="network-icon-img"
                  :alt="network.name"
                  @error="onImgError($event, network.slug)"
                />
              </div>

              <!-- Coin name & network label -->
              <div class="network-label">
                <span class="network-name">{{ parseName(network.name).coin }}</span>
                <span class="network-sub">{{ parseName(network.name).label }}</span>
              </div>

              <!-- Selected check -->
              <v-icon
                v-if="selectedNetwork === network.slug"
                class="network-check"
                color="#039DE9"
                size="18"
              >mdi-check-circle</v-icon>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="!loadingNetworks && (!ovypaycrypto || ovypaycrypto.length === 0)" class="text-center py-8">
            <v-icon size="48" color="grey">mdi-wifi-off</v-icon>
            <p class="mt-3 text--secondary">No networks available. Please try again later.</p>
          </div>

          <!-- Continue Button — centered pill -->
          <div class="continue-wrap mt-6">
            <v-btn
              class="continue-btn white--text"
              color="greenMain"
              :disabled="!selectedNetwork || loadingNetworks"
              @click="handleContinue"
              rounded
              large
            >
              Continue
              <v-icon right class="white--text">mdi-arrow-right</v-icon>
            </v-btn>
          </div>

        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { CRYPTO__ACTIONS } from "@/store/action-types";

// Local fallback icons — used only when file_url is missing or fails to load
const NETWORK_META = {
  // "usdt_trc20": {
  //   icon: require("@/assets/icons/trc20.png"),
  //   // badge: "Fast",
  // },
  // "btc": {
  //   icon: require("@/assets/icons/bitcoin.png"),
  //   badge: "Top",
  // },
};

const DEFAULT_META = {
  icon: require("@/assets/icons/dollar.png"),
  badge: null,
};

export default {
  name: "OvyPayNetworkForm",

  data() {
    return {
      selectedNetwork: null,
      selectedNetworkName: null,
      loadingNetworks: false,
    };
  },

  computed: {
    ...mapState("crypto", ["ovypaycrypto", "ovypayClientUrl"]),

    // Dynamic grid class based on number of networks
    gridClass() {
      const count = this.ovypaycrypto?.length || 0;
      if (count === 1) return "grid-cols-1";
      if (count === 2) return "grid-cols-2";
      return "grid-cols-3";
    },
  },

  methods: {
    ...mapActions("crypto", [CRYPTO__ACTIONS.OVYPAY]),

    // Returns local fallback meta for a slug
    getNetworkMeta(slug) {
      return NETWORK_META[slug] || DEFAULT_META;
    },

    // If remote file_url fails to load, fall back to local icon
    onImgError(event, slug) {
      const meta = this.getNetworkMeta(slug);
      event.target.src = meta.icon;
    },

    // "USDT (TRC-20)" → { coin: "USDT", label: "TRC-20" }
    parseName(name) {
      const match = name.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) return { coin: match[1].trim(), label: match[2].trim() };
      return { coin: name, label: "" };
    },

    selectNetwork(network) {
      this.selectedNetwork = network.slug;
      this.selectedNetworkName = network.name;
    },

    handleContinue() {
      if (!this.selectedNetwork) return;
      this.$router.push({
        name: "CryptoDepositOvyPayForm",
        query: {
          network:      this.selectedNetwork,
          network_name: this.selectedNetworkName,
        },
      });
    },
  },

  async created() {
    try {
      this.loadingNetworks = true;
      await this[CRYPTO__ACTIONS.OVYPAY]();
    } catch (error) {
      console.error("Failed to load crypto networks:", error);
    } finally {
      this.loadingNetworks = false;
    }
  },
};
</script>

<style scoped>
.main {
  min-height: 100vh;
  overflow-y: auto;
  margin: 0;
}

.network-form-card {
  border-radius: 12px;
}

.network-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.network-subtitle {
  font-size: 0.875rem;
  color: #888;
  margin: 0;
}

/* Dynamic grid layouts */
.network-grid {
  display: grid;
  gap: 12px;
}

.grid-cols-1 {
  grid-template-columns: minmax(0, 180px);
  justify-content: center;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, 1fr);
  max-width: 320px;
  margin: 0 auto;
}

.grid-cols-3 {
  grid-template-columns: repeat(3, 1fr);
}

@media (max-width: 480px) {
  .grid-cols-3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Card */
.network-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 8px 12px;
  border-radius: 10px;
  border: 2px solid transparent;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  min-height: 100px;
}

.network-card--dark {
  background: #1e2d3f;
}

.network-card:hover {
  border-color: #039DE9;
  box-shadow: 0 2px 12px rgba(3, 157, 233, 0.18);
  transform: translateY(-2px);
}

.network-card--selected {
  border-color: #039DE9 !important;
  box-shadow: 0 0 0 2px rgba(3, 157, 233, 0.25);
}

/* Badge */
.network-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  letter-spacing: 0.3px;
}

.badge--fast {
  background: #4CAF50;
  color: white;
}

.badge--top {
  background: #e3f2fd;
  color: #039DE9;
}

/* Icon */
.network-icon-wrap {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.network-icon-img {
  object-fit: contain;
  border-radius: 50%;
  display: block;
}

/* Label */
.network-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.network-name {
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.3;
}

.network-sub {
  font-size: 0.72rem;
  color: #888;
  line-height: 1.2;
}

/* Check icon */
.network-check {
  position: absolute;
  top: 6px;
  left: 6px;
}

/* Continue button — centered pill */
.continue-wrap {
  display: flex;
  justify-content: center;
}

.continue-btn:disabled:not(.continue-btn--active) {
  pointer-events: none;
}
</style>